import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import Reveal from './Reveal';
import SectionHeader from './SectionHeader';
import EditableText from './EditableText';
import { BTN_PRIMARY, BTN_OUTLINE, RADIUS_SURFACE } from '../config/brand';

const RobotGallery = ({
  projects,
  title = 'Nos projets robotiques',
  titleKey,
  subtitle = 'Plus de 50 modèles Makeblock et Artec réalisés par nos élèves',
  subtitleKey,
  eyebrow = 'Galerie',
  eyebrowKey,
  itemNameKeyPrefix,

  limit,
  showExpand = false,
  expandStep = 12,
  cta,
  ctaKey,
  id,
}) => {
  const [visible, setVisible] = useState(limit ?? projects.length);
  const items = projects.slice(0, visible);
  const hasMore = showExpand && visible < projects.length;
  return (
    <section id={id} className="section-y scroll-mt-site-header px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow={eyebrow}
          eyebrowKey={eyebrowKey}
          title={title}
          titleKey={titleKey}
          subtitle={subtitle}
          subtitleKey={subtitleKey}
          reveal
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((project, i) => {
            const n = i + 1;
            const nameKey = itemNameKeyPrefix
              ? `${itemNameKeyPrefix}.${n}.name`
              : null;
            const itemTitleKey = itemNameKeyPrefix
              ? `${itemNameKeyPrefix}.${n}.title`
              : null;

            return (
              <Reveal key={project.id} animation="fade-up" delay={(i % 4) * 40}>
                <figure className={`group relative aspect-square overflow-hidden ${RADIUS_SURFACE} bg-slate-100`}>
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#18449e]/90 via-[#22266a]/40 to-transparent px-3 pb-3 pt-10">
                    {itemTitleKey ? (
                      <EditableText
                        editKey={itemTitleKey}
                        defaultValue={project.title}
                        as="p"
                        className="text-white text-sm font-medium leading-tight"
                      />
                    ) : (
                      <p className="text-white text-sm font-medium leading-tight">
                        {project.title}
                      </p>
                    )}
                    {project.category &&
                      (nameKey ? (
                        <EditableText
                          editKey={nameKey}
                          defaultValue={project.category}
                          as="p"
                          className="mt-0.5 text-[11px] capitalize text-white/75"
                        />
                      ) : (
                        <p className="mt-0.5 text-[11px] capitalize text-white/75">
                          {project.category}
                        </p>
                      ))}
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>

        {(hasMore || cta) && (
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            {hasMore && (
              <Button
                variant="outline"
                className={BTN_OUTLINE}
                onClick={() =>
                  setVisible((n) => Math.min(n + expandStep, projects.length))
                }
              >
                Voir plus ({projects.length - visible})
              </Button>
            )}
            {cta && (
              <Link to={cta.to}>
                <Button className={BTN_PRIMARY}>
                  {ctaKey ? (
                    <EditableText editKey={ctaKey} defaultValue={cta.label} className="inline" />
                  ) : (
                    cta.label
                  )}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default RobotGallery;
